"use client"

import { useState, useEffect } from "react"
import {
  FaGraduationCap,
  FaIndustry,
  FaGlobe,
  FaUsers,
  FaLightbulb,
  FaHandshake,
  FaChevronDown,
  FaChevronUp,
  FaQuoteLeft,
  FaAward,
  FaRocket,
  FaShieldAlt,
  FaCheckCircle,
  FaEye,
} from "react-icons/fa"

const About = () => {
  const [expandedSections, setExpandedSections] = useState({})
  const [scrollY, setScrollY] = useState(0)
  const [visibleSections, setVisibleSections] = useState(new Set())

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const aboutSections = [
    {
      id: "pillar",
      title: "A Pillar of Excellence",
      icon: FaAward,
      gradient: "from-blue-500 to-blue-700",
      content:
        "Established with the aim of promoting excellence in the field of Chemical Engineering, IIChE has consistently played a crucial role in advancing education, research and professional development. By providing a platform for collaboration and knowledge exchange, the institute has contributed significantly to the growth and innovation within the discipline.",
    },
    {
      id: "adapting",
      title: "Adapting to Change",
      icon: FaRocket,
      gradient: "from-purple-500 to-purple-700",
      content:
        "In a world marked by constant change, IIChE has been proactive in adjusting its strategies to align with the evolving landscape of Chemical Engineering. The institute recognizes the importance of staying ahead of the curve, incorporating the latest technological advancements and fostering a culture of continuous learning among its members.",
    },
    {
      id: "objectives",
      title: "Fundamental Objectives",
      icon: FaLightbulb,
      gradient: "from-orange-500 to-orange-700",
      content:
        "The fundamental objectives of IIChE have remained steadfast throughout its journey. These include promoting excellence in education and research, encouraging innovation and facilitating the application of Chemical Engineering principles to address real-world challenges. The institute has been successful in nurturing a community of professionals committed to the highest standards of ethics and integrity.",
    },
    {
      id: "vision",
      title: "Vision for Advancement",
      icon: FaEye,
      gradient: "from-green-500 to-green-700",
      content:
        "At the core of IIChE's mission is the advancement of Chemical Engineering. The institute strives to push the boundaries of knowledge, encouraging research that not only contributes to academic excellence but also addresses pressing issues facing society. By fostering an environment that values creativity and exploration, IIChE aims to be a catalyst for groundbreaking discoveries in the field.",
    },
    {
      id: "ethics",
      title: "Ethical Foundation",
      icon: FaShieldAlt,
      gradient: "from-red-500 to-red-700",
      content:
        "Recognizing the profound impact that Chemical Engineering can have on society, IIChE places a strong emphasis on ethical considerations within the profession. The institute is dedicated to formulating and upholding a code of ethics that guides professionals in their practice, ensuring responsible and sustainable contributions to the community at large.",
    },
    {
      id: "global",
      title: "Global Connections",
      icon: FaGlobe,
      gradient: "from-indigo-500 to-indigo-700",
      content:
        "IIChE's commitment to global collaboration is evident in its robust connections with various professional bodies worldwide. The institute has forged collaborative agreements with esteemed institutions such as the Institution of Chemical Engineers in the UK, the American Institute of Chemical Engineers (AIChE) in the USA, the Canadian Institute of Chemical Engineers (CSChE) in Canada and the Society of Chemical Engineers, Japan (SCEJ). This strategic network underscores IIChE's dedication to fostering international cooperation and knowledge sharing in the realm of Chemical Engineering.",
    },
    {
      id: "collaboration",
      title: "International Collaborations",
      icon: FaHandshake,
      gradient: "from-teal-500 to-teal-700",
      content:
        "Through its collaborations, IIChE aims to create a global community of Chemical Engineering professionals. The exchange of ideas, best practices and research findings across borders enriches the collective knowledge pool and promotes a diversity of perspectives. These collaborations also provide opportunities for professionals to engage in international conferences, workshops and research initiatives, contributing to a more interconnected and dynamic global Chemical Engineering community.",
    },
    {
      id: "knowledge",
      title: "Knowledge Sharing and Continuous Interaction",
      icon: FaUsers,
      gradient: "from-pink-500 to-pink-700",
      content:
        "Facilitating continuous interaction and idea exchange among chemical engineering professionals in India and beyond is a primary focus for IIChE. The institute organizes conferences, seminars and workshops that serve as platforms for professionals to share their expertise, discuss emerging trends, and explore collaborative opportunities. By creating spaces for dialogue, IIChE fosters a sense of community and ensures that its members stay at the forefront of advancements in the field.",
    },
    {
      id: "industry",
      title: "Staying Connected with Industry",
      icon: FaIndustry,
      gradient: "from-yellow-500 to-yellow-700",
      content:
        "Recognizing the symbiotic relationship between academia and industry, IIChE places a strong emphasis on maintaining close connections with the industrial sector. This connection ensures that academic research aligns with the practical needs of the industry, promoting the seamless application of innovative solutions to real-world challenges. Industry professionals, in turn, benefit from the latest research findings and advancements presented by the academic community.",
    },
    {
      id: "development",
      title: "Commitment to Professional Development",
      icon: FaGraduationCap,
      gradient: "from-cyan-500 to-cyan-700",
      content:
        "IIChE is dedicated to the ongoing professional development of its members. The institute provides opportunities for skill enhancement, career guidance and mentorship programs, ensuring that professionals at all career stages have access to resources that support their growth and success in the field.",
    },
    {
      id: "future",
      title: "Looking Towards the Future",
      icon: FaRocket,
      gradient: "from-emerald-500 to-emerald-700",
      content:
        "As IIChE continues to evolve, it remains committed to its mission of advancing Chemical Engineering, promoting ethical practices and fostering international collaboration. The institute's dedication to excellence, adaptability to change and emphasis on global connections position it as a driving force in shaping the future of Chemical Engineering. Through its multifaceted approach, IIChE not only celebrates the achievements of the past but also paves the way for a vibrant and innovative future for the discipline and its professionals.",
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 dark:bg-green-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 dark:bg-blue-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-200 dark:bg-purple-800 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Hero Section with Parallax */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-green-600 via-green-700 to-blue-800"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-black/30"></div>

        {/* Animated Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`,
              }}
            ></div>
          ))}
        </div>

        <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-8 animate-bounce-slow">
            <FaGraduationCap className="w-12 h-12" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in-up">
            About <span className="text-green-300 animate-pulse">IIChE</span>
          </h1>
          <div className="w-24 h-1 bg-green-300 mx-auto mb-8 animate-expand"></div>
          <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed animate-fade-in-up animation-delay-500">
            The Indian Institute of Chemical Engineers
          </p>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Introduction with Enhanced Design */}
        <div
          data-animate
          id="intro"
          className={`relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-10 mb-16 overflow-hidden transform transition-all duration-700 ${
            visibleSections.has("intro") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-green-200 to-blue-200 dark:from-green-800 dark:to-blue-800 rounded-full opacity-20"></div>

          <div className="relative flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                <FaQuoteLeft className="w-8 h-8 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-6">
                IIChE:
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                The Indian Institute of Chemical Engineers (IIChE) stands as a pivotal convergence point, bringing
                together professionals from academia, research institutes, and industry within the dynamic realm of
                Chemical Engineering. Since its inception, IIChE has adapted proactively to the evolving needs of
                society and the economy while staying true to its fundamental objectives. The overarching vision of
                IIChE is to propel the advancement of Chemical Engineering and formulate a robust code of ethics within
                the profession.
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Expandable Sections */}
        <div className="space-y-8">
          <div
            data-animate
            id="sections-header"
            className={`text-center transform transition-all duration-700 ${
              visibleSections.has("sections-header") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-4">
              Our Journey & Vision
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-8"></div>
          </div>

          {aboutSections.map((section, index) => (
            <div
              key={section.id}
              data-animate
              id={`section-${section.id}`}
              className={`group relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden transform transition-all duration-700 hover:shadow-2xl ${
                visibleSections.has(`section-${section.id}`) ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${section.gradient}`}></div>

              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-8 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div
                      className={`relative p-4 bg-gradient-to-r ${section.gradient} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <section.icon className="w-8 h-8 text-white" />
                      <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                        {section.title}
                      </h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <FaCheckCircle className="w-4 h-4" />
                        <span>Click to {expandedSections[section.id] ? "collapse" : "expand"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-green-500 transition-colors duration-300">
                    {expandedSections[section.id] ? (
                      <FaChevronUp className="w-6 h-6 transform group-hover:scale-110 transition-transform duration-300" />
                    ) : (
                      <FaChevronDown className="w-6 h-6 transform group-hover:scale-110 transition-transform duration-300" />
                    )}
                  </div>
                </div>
              </button>

              {expandedSections[section.id] && (
                <div className="px-8 pb-8 animate-fade-in">
                  <div className="pl-20">
                    <div className={`relative border-l-4 border-gradient-to-b ${section.gradient} pl-8 py-4`}>
                      <div className="absolute -left-2 top-6 w-4 h-4 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"></div>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">{section.content}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes expand {
          from { width: 0; }
          to { width: 6rem; }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
        .animate-expand { animation: expand 1s ease-out 1s forwards; width: 0; }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .animation-delay-500 { animation-delay: 0.5s; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .shadow-3xl { box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25); }
      `}</style>
    </div>
  )
}

export default About
